import type { ChatService } from './chat-service'
import type { ChatContext, StreamChunk, CliAction } from '@/store/chat-types'
import type { CompositeAction } from './composite-action'
import type { WorkspaceMode } from '@/store/types'
import type { StorySectionId } from '@/store/story-types'
import type { ConversationPhase } from '@/store/conversation-types'
import { buildAppSpecFromPrompt } from './app-spec-from-prompt'
import { buildStudioReleaseDeterministicIntent } from './studio-release-chat-intent'
import { hasStudioChatMutationIntent } from './studio-chat-mutation-intent'

export interface IntentResponse {
  text: string
  actions?: CliAction[]
  targetMode?: WorkspaceMode
  scrollTarget?: StorySectionId
}

/**
 * An intent the CLIENT-DETERMINISTIC runner can narrate — at most one action.
 *
 * `runClientDeterministicIntent` (chat-model-adapter.ts) has exactly ONE
 * sentence, `text`, for the whole of `actions`. With two or more actions that
 * sentence stops corresponding to an outcome, and both ways of composing it
 * are false: derived from the failures it says "nothing was changed" over an
 * action that already landed (I-4), and derived from the intent it claims the
 * whole thing was applied when the run stopped after the first action (I-5).
 * Neither can be told truthfully without per-action copy that does not exist,
 * so the shape is made unrepresentable rather than mis-narrated.
 *
 * `IntentResponse` itself stays N-ary: `getPipelineResponse` below builds
 * `[...blockActions, setPhase]` from a mapped page list, and that path runs
 * through `MockChatService`, which narrates each action separately.
 *
 * Multi-step deterministic work has a home that needs none of this: ONE action
 * carrying many RFC6902 patches (`buildPageIntent` in live-cogo-spec-intent.ts
 * sends a single `applyAppPatch` with two). That is atomic at the operation
 * boundary, so one sentence really does describe its outcome.
 *
 * Compile-time enforcement: `single-action-intent.contract.ts`.
 */
export type SingleActionIntentResponse = Omit<IntentResponse, 'actions'> & {
  actions?: [] | [CliAction]
}

interface IntentPattern {
  pattern: RegExp
  // Narrow, not `IntentResponse`: every entry here is reachable from
  // `detectClientDeterministicIntent`, whose values are narrated one sentence
  // per turn. `detectIntent` (the MockChatService path) reads the same table
  // and is unaffected — a one-action intent is still an `IntentResponse`.
  response: SingleActionIntentResponse
  clientDeterministic?: boolean
}

function shouldGenerateActionTaskApp(content: string, context?: ChatContext): boolean {
  const haystack = [
    content,
    context?.appName,
    context?.narrative,
    ...(context?.goals ?? []),
  ].filter(Boolean).join(' ')

  const asksForGeneration = /\bgenerate\s+(?:the\s+)?app\b/i.test(content)
  const taskIntent = /\b(task|todo|to-do)\b/i.test(haystack) && /\b(toggle|check|complete|completion)\b/i.test(haystack)

  return taskIntent || asksForGeneration
}

function buildActionTaskAppGenerationAction(context?: ChatContext, content = ''): CliAction {
  const prompt = context?.narrative || context?.appName || content || 'Generated App'
  const plan = buildAppSpecFromPrompt(prompt)
  const appName = context?.appName || plan.appSpec.name
  return {
    type: 'requestAppGeneration',
    payload: {
      appSpec: {
        ...plan.appSpec,
        name: appName,
        description: context?.narrative || plan.appSpec.description,
      },
    },
  }
}

function isAddTextElementIntent(content: string): boolean {
  const asksForTextElement = /(text|label|copy|headline|title|문구|텍스트|라벨|제목)/i.test(content)
  const asksToAdd = /(add|create|insert|show|display|추가|생성|삽입|표시|보여)/i.test(content)
  return asksForTextElement && asksToAdd
}

function buildExactTextElementIntent(content: string): SingleActionIntentResponse | null {
  if (!isAddTextElementIntent(content)) return null
  const text = extractQuotedText(content)
  if (!text) return null
  return {
    text: `Added "${text}" and refreshed the app.`,
    actions: [
      {
        type: 'addElement',
        payload: { elementType: 'text', content: text, properties: { text } },
      },
    ],
    targetMode: 'page',
  }
}

function buildTextElementClarification(content: string): SingleActionIntentResponse | null {
  if (!isAddTextElementIntent(content) || extractQuotedText(content)) return null

  const korean = /[가-힣]/.test(content)
  return {
    text: korean
      ? '추가할 정확한 문구를 알려주세요.'
      : 'What text should I add? Send the exact words in quotes, for example: Add "Welcome".',
  }
}

function extractQuotedText(content: string): string | null {
  const match = content.match(/["“”'`「『](.+?)["“”'`」』]/)
  return match?.[1]?.trim() || null
}

const THEME_PRESET_ALIASES = [
  { id: 'warm', pattern: /\bwarm\b|따뜻/i },
  { id: 'modern', pattern: /\bmodern\b|\bclean\b|모던/i },
  { id: 'professional', pattern: /\bprofessional\b|\bbusiness\b|프로페셔널|비즈니스/i },
  { id: 'vibrant', pattern: /\bvibrant\b|\bvivid\b|\bcolorful\b|생생|컬러풀/i },
  { id: 'minimal', pattern: /\bminimal\b|미니멀/i },
] as const

const THEME_COLOR_KEYS = [
  'background',
  'foreground',
  'primary',
  'primaryForeground',
  'secondary',
  'secondaryForeground',
  'accent',
  'accentForeground',
  'muted',
  'mutedForeground',
  'destructive',
  'destructiveForeground',
  'border',
  'input',
  'ring',
  'card',
  'cardForeground',
  'popover',
  'popoverForeground',
] as const

function exactTrailingValue(content: string, marker: RegExp): string | null {
  const match = content.match(marker)
  const value = match?.[1]?.trim().replace(/^["“”'`「『]|["“”'`」』]$/g, '')
  return value || null
}

function guidance(text: string): SingleActionIntentResponse {
  return { text }
}

/**
 * Route only commands whose operation and required values are explicit.
 * Every mutation uses the fully-qualified WebMCP tool name, so the shared
 * capability gateway supplies confirmation instead of executing it directly.
 */
function buildThemeProjectDeterministicIntent(content: string): SingleActionIntentResponse | null {
  const prompt = content.trim()
  const mentionsTheme = /\btheme\b|테마/i.test(prompt)
  const mentionsProject = /\bproject\b|프로젝트/i.test(prompt)

  if (mentionsTheme) {
    if (/(?:reset|default|초기화|기본).*(?:theme|테마)|(?:theme|테마).*(?:reset|default|초기화|기본)/i.test(prompt)) {
      return {
        text: 'The application theme reset is ready for confirmation. This changes only the local working copy until server persistence is verified.',
        actions: [{ type: 'cogo.studio.resetTheme', payload: {} }],
      }
    }

    const preset = THEME_PRESET_ALIASES.find((candidate) => candidate.pattern.test(prompt))
    if (preset) {
      return {
        text: `The ${preset.id} application theme is ready for confirmation.`,
        actions: [{ type: 'cogo.studio.setThemePreset', payload: { presetId: preset.id } }],
      }
    }

    const colorValue = prompt.match(/#[0-9a-f]{3,8}\b/i)?.[0]
    const colorKey = THEME_COLOR_KEYS.find((key) =>
      new RegExp(`\\b${key}\\b`, 'i').test(prompt),
    )
    if (/\bcolor\b|색상|컬러/i.test(prompt)) {
      if (!colorKey || !colorValue) {
        return guidance('Name one exact theme color token and a hex value, for example: Set theme primary color to #4F46E5.')
      }
      return {
        text: `The ${colorKey} theme color change to ${colorValue} is ready for confirmation.`,
        actions: [{
          type: 'cogo.studio.updateThemeColor',
          payload: { key: colorKey, value: colorValue },
        }],
      }
    }

    if (/\bfont\b|폰트/i.test(prompt)) {
      const fontFamily = extractQuotedText(prompt) ?? exactTrailingValue(
        prompt,
        /(?:font|폰트)\s*(?:to|as|=|:|으로|로)\s*(.+)$/i,
      )
      if (!fontFamily) {
        return guidance('Name the exact font family, for example: Set theme font to "Pretendard".')
      }
      return {
        text: `The application font change to ${fontFamily} is ready for confirmation.`,
        actions: [{ type: 'cogo.studio.setFont', payload: { fontFamily } }],
      }
    }

    if (/\bradius\b|모서리|라디우스/i.test(prompt)) {
      const radius = prompt.match(/\b(none|sm|md|lg)\b/i)?.[1]?.toLowerCase()
      if (!radius) {
        return guidance('Choose an exact radius scale: none, sm, md, or lg.')
      }
      return {
        text: `The application radius change to ${radius} is ready for confirmation.`,
        actions: [{ type: 'cogo.studio.setRadius', payload: { radius } }],
      }
    }
  }

  if (!mentionsProject) return null

  if (/(?:delete|remove|삭제).*(?:project|프로젝트)|(?:project|프로젝트).*(?:delete|remove|삭제)/i.test(prompt)) {
    const projectId = exactTrailingValue(
      prompt,
      /(?:project|프로젝트)\s*(?:id|아이디)\s*(?:=|:|is|는|은)?\s*(.+)$/i,
    )
    if (!projectId) {
      return guidance('Project deletion needs the exact project ID. Select the project or say: Delete project ID "...".')
    }
    return {
      text: `Project ${projectId} deletion is prepared. Studio will require typed DELETE confirmation and still needs server readback before claiming persistence.`,
      actions: [{ type: 'cogo.studio.deleteProject', payload: { projectId } }],
    }
  }

  if (/(?:open|load|열기).*(?:project|프로젝트)|(?:project|프로젝트).*(?:open|load|열기)/i.test(prompt)) {
    const projectId = exactTrailingValue(
      prompt,
      /(?:project|프로젝트)\s*(?:id|아이디)\s*(?:=|:|is|는|은)?\s*(.+)$/i,
    )
    if (!projectId) {
      return guidance('Opening a project needs its exact project ID. Select it in Studio or say: Open project ID "...".')
    }
    return {
      text: `Opening project ${projectId}.`,
      actions: [{ type: 'cogo.studio.openProject', payload: { projectId } }],
    }
  }

  if (/(?:close|exit|닫기|종료).*(?:project|프로젝트)|(?:project|프로젝트).*(?:close|exit|닫기|종료)/i.test(prompt)) {
    return {
      text: 'Closing the active Studio project without deleting it.',
      actions: [{ type: 'cogo.studio.closeProject', payload: {} }],
    }
  }

  if (/(?:rename|이름\s*변경).*(?:project|프로젝트)|(?:project|프로젝트).*(?:rename|이름\s*변경)/i.test(prompt)) {
    const name = extractQuotedText(prompt) ?? exactTrailingValue(
      prompt,
      /(?:rename\s+(?:the\s+)?project|project\s+(?:name\s+)?(?:to|as)|프로젝트\s*이름(?:을|를)?\s*)(?:to|as|=|:|으로|로)?\s*(.+)$/i,
    )
    if (!name) {
      return guidance('Name the exact new project name, for example: Rename project to "Customer Portal".')
    }
    return {
      text: `The project rename to ${name} is ready for confirmation.`,
      actions: [{ type: 'cogo.studio.renameProject', payload: { name } }],
    }
  }

  if (/\bdescription\b|설명/i.test(prompt) && hasStudioChatMutationIntent(prompt)) {
    const description = extractQuotedText(prompt) ?? exactTrailingValue(
      prompt,
      /(?:description|설명)\s*(?:to|as|=|:|으로|로)\s*(.+)$/i,
    )
    if (!description) {
      return guidance('Provide the exact project description, preferably in quotes.')
    }
    return {
      text: 'The project description update is ready for confirmation.',
      actions: [{ type: 'cogo.studio.updateProjectMeta', payload: { description } }],
    }
  }

  if (/\bicon\b|아이콘/i.test(prompt) && /(?:set|update|change|변경|수정)/i.test(prompt)) {
    const icon = extractQuotedText(prompt) ?? exactTrailingValue(
      prompt,
      /(?:icon|아이콘)\s*(?:to|as|=|:|으로|로)\s*(.+)$/i,
    )
    if (!icon) return guidance('Provide the exact project icon value.')
    return {
      text: 'The project icon update is ready for confirmation.',
      actions: [{ type: 'cogo.studio.updateProjectMeta', payload: { icon } }],
    }
  }

  if (/(?:duplicate|copy|복제).*(?:project|프로젝트)|(?:project|프로젝트).*(?:duplicate|copy|복제)/i.test(prompt)) {
    const newName = extractQuotedText(prompt)
    return {
      text: 'The active project copy is ready for confirmation.',
      actions: [{
        type: 'cogo.studio.duplicateProject',
        payload: newName ? { newName } : {},
      }],
    }
  }

  if (/(?:create|new|make|생성|만들).*(?:project|프로젝트)|(?:project|프로젝트).*(?:create|new|make|생성|만들)/i.test(prompt)) {
    const name = extractQuotedText(prompt) ?? exactTrailingValue(
      prompt,
      /(?:project|프로젝트)\s*(?:named|called|name|이름)?\s*(?:=|:|is|은|는)?\s*["“”'`「『](.+?)["“”'`」』]\s*$/i,
    )
    if (!name) {
      return guidance('Project creation needs an exact name, for example: Create project "Customer Portal".')
    }
    return {
      text: `Project ${name} creation is ready for confirmation.`,
      actions: [{ type: 'cogo.studio.createProject', payload: { name } }],
    }
  }

  return null
}

// ─── Simple intent patterns (backward-compatible) ──────────────

const intentPatterns: IntentPattern[] = [
  {
    pattern: /button.*(add|create|insert)|add.*button/i,
    clientDeterministic: true,
    response: {
      text: "I'll add a button. Please check in the Page Editor.",
      actions: [
        { type: 'addElement', payload: { elementType: 'button', label: 'New Button', properties: { label: 'New Button' } } },
      ],
      targetMode: 'page',
    },
  },
  {
    pattern: /text.*(add|create|insert)|add.*text/i,
    clientDeterministic: true,
    response: {
      text: "I'll add a text element.",
      actions: [
        {
          type: 'addElement',
          payload: { elementType: 'text', content: 'Hello World', properties: { text: 'Hello World' } },
        },
      ],
      targetMode: 'page',
    },
  },
  {
    pattern: /(\bflow\b|actionflow|액션플로우|플로우).*(create|add|make|생성|추가)|(create|add|make|생성|추가).*(\bflow\b|actionflow|액션플로우|플로우)/i,
    clientDeterministic: true,
    response: {
      text: "I'll create a new ActionFlow. Please check in the Flow Editor.",
      actions: [{ type: 'addFlow', payload: { name: 'Chat Created Flow' } }],
      targetMode: 'flow',
    },
  },
  {
    pattern: /ontology|application model|model map|knowledge|온톨로지|온토로지/i,
    clientDeterministic: true,
    response: {
      text: 'Opening the Application model so you can see Page, data, ActionFlow, and validation relationships.',
      actions: [{ type: 'openIdeSurface', payload: { surface: 'ontology' } }],
    },
  },
  {
    pattern: /preview/i,
    clientDeterministic: true,
    response: {
      text: 'Showing app preview.',
      targetMode: 'preview',
    },
  },
  {
    pattern: /page.*edit|edit.*page/i,
    clientDeterministic: true,
    response: {
      text: 'Switching to the page editor.',
      targetMode: 'page',
    },
  },
  {
    pattern: /story|narrative/i,
    response: {
      text: 'Showing the story narrative section.',
      scrollTarget: 'narrative' as StorySectionId,
    },
  },
  {
    pattern: /goals?/i,
    response: {
      text: 'Showing the story goals section.',
      scrollTarget: 'goals' as StorySectionId,
    },
  },
  {
    pattern: /scenario|test/i,
    response: {
      text: 'Showing the interaction scenarios section.',
      scrollTarget: 'bdd' as StorySectionId,
    },
  },
  {
    pattern: /chapter|page.*list/i,
    response: {
      text: 'Showing the page list.',
      scrollTarget: 'pages' as StorySectionId,
    },
  },
  {
    pattern: /(deploy|publish)/i,
    response: {
      text: 'Publishing the project. Please wait.',
      actions: [{ type: 'publishProject', payload: {} }],
    },
  },
  // ── Variable / Datasource commands ──
  {
    pattern: /variable.*(add|create|make)/i,
    response: {
      text: 'Adding a new variable.',
      actions: [{ type: 'addVariable', payload: { scope: 'state', name: 'newVar', type: 'string', default: '' } }],
    },
  },
  {
    pattern: /datasource.*(add|create|connect)/i,
    response: {
      text: 'Connecting a new datasource.',
      actions: [{ type: 'addDatasource', payload: { name: 'apiSource', type: 'rest', url: 'https://api.example.com/data' } }],
    },
  },
  {
    pattern: /event.*(add|create|bind|connect)/i,
    response: {
      text: 'Adding a new event binding.',
      actions: [{ type: 'addEventBinding', payload: { eventKey: 'onTap', flow: 'handleTap' } }],
    },
  },
  {
    pattern: /event.*(delete|remove|unbind)/i,
    response: {
      text: 'Removing the event binding.',
      actions: [{ type: 'removeEventBinding', payload: { eventKey: 'onTap' } }],
    },
  },
]

const defaultResponse: IntentResponse = {
  text: 'Sure, how can I help? You can add elements to a page, create an ActionFlow, or check the preview.',
}

function detectIntent(content: string): IntentResponse {
  const exactTextIntent = buildExactTextElementIntent(content)
  if (exactTextIntent) return exactTextIntent

  const clarification = buildTextElementClarification(content)
  if (clarification) return clarification

  const releaseIntent = buildStudioReleaseDeterministicIntent(content)
  if (releaseIntent) return releaseIntent

  const themeProjectIntent = buildThemeProjectDeterministicIntent(content)
  if (themeProjectIntent) return themeProjectIntent

  for (const { pattern, response } of intentPatterns) {
    if (pattern.test(content)) {
      return response
    }
  }
  return defaultResponse
}

export function detectClientDeterministicIntent(
  content: string,
): SingleActionIntentResponse | null {
  const exactTextIntent = buildExactTextElementIntent(content)
  if (exactTextIntent) return exactTextIntent

  const clarification = buildTextElementClarification(content)
  if (clarification) return clarification

  const releaseIntent = buildStudioReleaseDeterministicIntent(content)
  if (releaseIntent) return releaseIntent

  const themeProjectIntent = buildThemeProjectDeterministicIntent(content)
  if (themeProjectIntent) return themeProjectIntent

  for (const { pattern, response, clientDeterministic } of intentPatterns) {
    if (clientDeterministic && pattern.test(content)) {
      return response
    }
  }
  return null
}

// ─── Pipeline trigger detection ────────────────────────────────

const PIPELINE_TRIGGER = /create.*app|make.*app|build.*app/i

function isPipelineTrigger(content: string): boolean {
  return PIPELINE_TRIGGER.test(content)
}

// ─── Phase-aware pipeline responses ────────────────────────────

/** Extract app keywords from user content for contextual generation */
function extractAppKeywords(content: string): {
  appName: string
  domain: string
  features: string[]
} {
  // Try common app descriptions: "make a {name} app"
  const nameMatch = content.match(/(.+?)\s*(app)/i)
  const appName = nameMatch?.[1]?.trim() ?? 'New App'

  // Detect domain from keywords
  const domainMap: [RegExp, string][] = [
    [/coffee|cafe/i, 'Cafe'],
    [/shopping|product/i, 'Shopping'],
    [/delivery|food/i, 'Food Delivery'],
    [/booking|reservation/i, 'Booking'],
    [/social|SNS/i, 'SNS'],
    [/news|blog/i, 'News'],
    [/education|learning/i, 'Education'],
    [/fitness|workout/i, 'Fitness'],
    [/travel|trip/i, 'Travel'],
    [/finance|banking/i, 'Finance'],
  ]
  let domain = 'General'
  for (const [pattern, name] of domainMap) {
    if (pattern.test(content)) { domain = name; break }
  }

  // Extract explicit features from user text
  const featurePatterns = content.match(
    /(?:menu|order|delivery|payment|login|signup|search|notification|settings|tracking|management|browse|register|confirm|cart|review|favorites|profile|share|comments|likes|booking|schedule|calendar|stats|chat)[^\s,.]*/gi,
  )
  const features = featurePatterns ? [...new Set(featurePatterns)].slice(0, 8) : []

  return { appName, domain, features }
}

/** Domain-specific interaction scenario templates */
const DOMAIN_SCENARIOS: Record<string, Array<{ name: string; steps: Array<{ type: string; text: string }> }>> = {
  'Cafe': [
    { name: 'Browse Menu', steps: [
      { type: 'given', text: 'the user is on the cafe main page' },
      { type: 'when', text: 'they select a menu category (Coffee/Non-Coffee/Dessert)' },
      { type: 'then', text: 'the menu list for that category is displayed with images, names, and prices' },
      { type: 'and', text: 'popular items show a "BEST" badge' },
    ]},
    { name: 'Drink Customization', steps: [
      { type: 'given', text: 'the user has selected the Americano menu item' },
      { type: 'when', text: 'they set size (Tall/Grande/Venti), temperature (Hot/Ice), and extra shot options' },
      { type: 'then', text: 'the final price updates in real time based on the selected options' },
      { type: 'and', text: 'the "Add to Cart" button is enabled' },
    ]},
    { name: 'Cart and Order', steps: [
      { type: 'given', text: 'the user has added 2 or more items to the cart' },
      { type: 'when', text: 'they tap the Place Order button' },
      { type: 'then', text: 'the order summary (items, quantity, total amount) is displayed' },
      { type: 'and', text: 'the order is placed after selecting a payment method' },
      { type: 'and', text: 'the order number and estimated preparation time are shown' },
    ]},
    { name: 'Order Status Tracking', steps: [
      { type: 'given', text: 'the user has completed an order' },
      { type: 'when', text: 'they check the order status page' },
      { type: 'then', text: 'the order status (Received > Preparing > Ready) is displayed in real time' },
      { type: 'and', text: 'a push notification is sent when the order is ready' },
    ]},
    { name: 'Points Earning and Redemption', steps: [
      { type: 'given', text: 'a logged-in user has completed a payment' },
      { type: 'when', text: 'they check the points history on their profile page' },
      { type: 'then', text: '5% of the payment amount has been earned as points' },
      { type: 'and', text: 'points can be used as a discount on the next order' },
    ]},
  ],
  'General': [
    { name: 'User Onboarding', steps: [
      { type: 'given', text: 'a first-time user has opened the app' },
      { type: 'when', text: 'they click the Get Started button' },
      { type: 'then', text: 'onboarding screens introducing the key features are displayed in sequence' },
      { type: 'and', text: 'they can proceed with Skip or Next buttons' },
    ]},
    { name: 'Content Browsing', steps: [
      { type: 'given', text: 'the user is on the main page' },
      { type: 'when', text: 'they select a category or enter a search term' },
      { type: 'then', text: 'related content is displayed as a list of cards' },
      { type: 'and', text: 'filtering and sorting options are available' },
    ]},
    { name: 'View Details', steps: [
      { type: 'given', text: 'the user has selected an item from the list' },
      { type: 'when', text: 'the detail page loads' },
      { type: 'then', text: 'images, descriptions, and related information are displayed in full screen' },
      { type: 'and', text: 'Share and Favorites buttons are displayed' },
    ]},
    { name: 'User Profile Management', steps: [
      { type: 'given', text: 'a logged-in user is on the profile page' },
      { type: 'when', text: 'they edit profile information and tap Save' },
      { type: 'then', text: 'the updated information is saved and a success message is displayed' },
    ]},
    { name: 'Settings and Notifications', steps: [
      { type: 'given', text: 'the user is on the settings page' },
      { type: 'when', text: 'they change notification settings' },
      { type: 'then', text: 'the changes are applied immediately' },
      { type: 'and', text: 'the app theme (Light/Dark) can be toggled' },
    ]},
  ],
}

/** Domain-specific page templates */
const DOMAIN_PAGES: Record<string, Array<{ title: string; description: string }>> = {
  'Cafe': [
    { title: 'Home', description: 'Main page with cafe introduction, recommended menu, and event banners' },
    { title: 'Menu', description: 'Menu list by category — images, names, prices, BEST badges' },
    { title: 'Menu Detail', description: 'Detailed info for selected menu item, size/temperature/shot customization options' },
    { title: 'Cart', description: 'Cart item list, quantity adjustment, total calculation, Place Order button' },
    { title: 'Order Status', description: 'Order status tracking (Received > Preparing > Ready), order number, estimated time' },
    { title: 'My Page', description: 'Points history, order history, favorite menu items, profile settings' },
  ],
  'General': [
    { title: 'Home', description: 'Main page with app introduction, recommended content, and category navigation' },
    { title: 'Browse', description: 'Content list by category — card layout, search, filtering' },
    { title: 'Detail', description: 'Detailed information for selected item — images, description, related items' },
    { title: 'My Page', description: 'Profile, settings, favorites, activity history' },
    { title: 'Settings', description: 'Notification settings, theme toggle, account management' },
  ],
}

function getPipelineResponse(
  phase: ConversationPhase,
  content: string,
  context?: ChatContext,
): IntentResponse {
  // Extract keywords from narrative context for richer responses
  const narrative = context?.narrative ?? ''
  const { appName, domain } = extractAppKeywords(narrative || content)

  switch (phase) {
    case 'idle':
    case 'gathering':
      return {
        text: `Great! What kind of app would you like to create?\n\nPlease describe the app name, purpose, and key features in detail.\nExample: "I want to create a cafe ordering app. I need menu browsing, customization, cart, and order tracking features."`,
        actions: [
          { type: 'setPhase', payload: { phase: 'gathering' } },
        ],
      }
    case 'scenarios': {
      const scenarios = (DOMAIN_SCENARIOS[domain] ?? DOMAIN_SCENARIOS['General']).map((s, i) => ({
        id: `s-${i + 1}`,
        ...s,
      }))
      return {
        text: `Generated ${scenarios.length} usage scenarios for the "${appName}" app.\n\nEach scenario is a runtime interaction contract, defining the core user flows of the app.\n\nPlease review the scenarios and say "next" or "generate pages".`,
        actions: [
          { type: 'generateBdd', payload: { scenarios } },
        ],
        scrollTarget: 'bdd' as StorySectionId,
      }
    }
    case 'pages': {
      const pageTemplates = DOMAIN_PAGES[domain] ?? DOMAIN_PAGES['General']
      const pages = pageTemplates.map((p, i) => ({
        id: `p-${i + 1}`,
        title: p.title,
        order: i,
        status: 'draft' as const,
        elementCount: 3 + i,  // Will be updated by handler
        description: p.description,
      }))
      return {
        text: `Generating ${pages.length} pages based on interaction scenarios.\n\n${pages.map((p, i) => `${i + 1}. **${p.title}** — ${p.description}`).join('\n')}\n\nEach page includes default layout and UI elements.`,
        actions: [
          { type: 'generatePages', payload: { pages } },
        ],
        scrollTarget: 'pages' as StorySectionId,
      }
    }
    case 'page-ui': {
      if (shouldGenerateActionTaskApp(content, context)) {
        return {
          text: `Generating the "${appName}" app with requested pages, blocks, and an ActionFlow contract.\n\nThe preview will use event bindings, local state, and renderer updates from the generated Coogo JSON.`,
          actions: [
            buildActionTaskAppGenerationAction(context, content),
            { type: 'setPhase', payload: { phase: 'complete' } },
          ],
        }
      }

      // Generate UI for each page via Cloudflare Block API
      const pages = context?.pages ?? []
      const blockActions = pages.map((p: any) => ({
        type: 'requestBlockGeneration' as const,
        payload: {
          name: `block-${p.id}`,
          type: p.title?.toLowerCase().includes('home') ? 'hero' : 'section',
          description: `${p.title}: ${p.description || p.title + ' page'}`,
        },
      }))

      return {
        text: `Generating rich UI for ${pages.length || 'each'} page(s) via Cloudflare API.\n\nEach block uses Tailwind CSS + shadcn/ui with 15-20 elements.\n\nThis may take 30-60 seconds per block.`,
        actions: [
          ...blockActions,
          { type: 'setPhase', payload: { phase: 'theming' } },
        ],
      }
    }
    case 'theming':
      return {
        text: `Applying a theme for the "${appName}" app.\n\n- Primary color: Indigo (#4F46E5)\n- Font: Inter\n- Style: Modern, clean design\n\nIf you'd like to change the theme, say "warm theme" or "minimal theme".`,
        actions: [
          {
            type: 'applyTheme',
            payload: { primaryColor: '#4F46E5', style: 'modern', fontFamily: 'Inter' },
          },
        ],
      }
    case 'generating':
      return {
        text: `Finalizing the "${appName}" app.\n\nAdding navigation bars and CTA buttons to each page, and confirming the final app structure.\n\nYou can check it in the preview once complete.`,
        actions: [
          { type: 'finalizeApp', payload: { appId: `app-${Date.now()}` } },
        ],
      }
    case 'complete':
      return {
        text: `The "${appName}" app has been created!\n\n- Check the narrative, interaction scenarios, and page structure in the Story tab\n- Edit each page's UI elements in the Page tab\n- Preview the app in the Preview tab\n\nLet me know if you need additional elements or flows!`,
      }
  }
}

function getConfirmNarrativeResponse(content: string, _context?: ChatContext): IntentResponse {
  const { appName, domain, features } = extractAppKeywords(content)
  const goals = features.length > 0 ? features : extractGoals(content)

  // Mock responses support deterministic interaction tests. They are not
  // ontology evidence and must never promote the generic catch-all templates
  // into a narrative that looks like an admitted application.
  if (appName === 'New App' || domain === 'General' || features.length === 0) {
    return {
      text: "I couldn't verify a source-backed application ontology for this request. No app, seed, or project was created. Please retry with the live ontology service available.",
      actions: [{ type: 'setPhase', payload: { phase: 'gathering' } }],
    }
  }

  return {
    text: `Understood the "${appName}" app!\n\n**App Name**: ${appName}\n**Domain**: ${domain}\n**Core Goals** (${goals.length}):\n${goals.map((g, i) => `  ${i + 1}. ${g}`).join('\n')}\n\nNarrative confirmed. Generating interaction scenarios next.`,
    actions: [
      {
        type: 'confirmNarrative',
        payload: {
          appName,
          narrative: content,
          goals,
        },
      },
    ],
    scrollTarget: 'narrative' as StorySectionId,
  }
}

function extractGoals(content: string): string[] {
  const goalPatterns = content.match(
    /(?:menu|order|delivery|payment|login|signup|search|notification|settings|tracking|management|browse|register|confirm|cart|review|favorites|profile|share|comments|likes|booking|schedule|calendar|stats|chat|customization|points|rewards|events|coupons)[^\s,.]*/gi,
  )
  if (goalPatterns && goalPatterns.length > 0) {
    return [...new Set(goalPatterns)].slice(0, 8)
  }
  return ['Content browsing', 'User management', 'Settings']
}

// ─── Composite intent patterns (context-aware) ────────────────

interface CompositeIntentResponse extends IntentResponse {
  composite: CompositeAction[]
}

const compositePatterns: { pattern: RegExp; buildResponse: (ctx: ChatContext) => CompositeIntentResponse }[] = [
  {
    // AUTO-01: button + API connection
    pattern: /button.*api.*connect|connect.*button.*api|버튼.*api.*연결|버튼.*연결/i,
    buildResponse: (ctx) => ({
      text: "I'll connect this button to an API call. Here's what I'll set up:",
      composite: [{
        intent: 'connect-button-to-api',
        description: 'Connect button tap to REST API call',
        items: [
          {
            category: 'datasource',
            summary: 'Add REST datasource "apiSource"',
            actions: [{ type: 'addDatasource', payload: { blockName: ctx.selectedBlockName ?? 'main', name: 'apiSource', type: 'rest', url: 'https://api.example.com/data', method: 'GET' } }],
          },
          {
            category: 'flow',
            summary: 'Create flow "fetchData"',
            actions: [{ type: 'addFlow', payload: { name: 'fetchData', trigger: 'event' } }],
          },
          {
            category: 'variable',
            summary: 'Add state variable "apiResponse"',
            actions: [{ type: 'addVariable', payload: { blockName: ctx.selectedBlockName ?? 'main', scope: 'state', name: 'apiResponse', type: 'object', default: null } }],
          },
          {
            category: 'event',
            summary: 'Bind onTap to fetchData flow',
            actions: [{ type: 'addEventBinding', payload: { blockName: ctx.selectedBlockName ?? 'main', eventKey: `${ctx.selectedElementId ?? 'button_1'}:onTap`, flow: 'fetchData' } }],
          },
        ],
      }],
    }),
  },
  {
    // AUTO-02: cart / shopping cart feature
    pattern: /장바구니|cart.*feature|\badd\s+(?:to\s+)?cart\b|shopping.*cart/i,
    buildResponse: (ctx) => ({
      text: "I'll add shopping cart functionality. Here's the full set:",
      composite: [{
        intent: 'add-cart-feature',
        description: 'Add shopping cart with state, datasource, and event bindings',
        items: [
          {
            category: 'variable',
            summary: 'Add state variable "cartItems" (array)',
            actions: [{ type: 'addVariable', payload: { blockName: ctx.selectedBlockName ?? 'main', scope: 'state', name: 'cartItems', type: 'array', default: [] } }],
          },
          {
            category: 'variable',
            summary: 'Add state variable "cartTotal" (number)',
            actions: [{ type: 'addVariable', payload: { blockName: ctx.selectedBlockName ?? 'main', scope: 'state', name: 'cartTotal', type: 'number', default: 0 } }],
          },
          {
            category: 'flow',
            summary: 'Create flow "addToCart"',
            actions: [{ type: 'addFlow', payload: { name: 'addToCart', trigger: 'event' } }],
          },
          {
            category: 'flow',
            summary: 'Create flow "removeFromCart"',
            actions: [{ type: 'addFlow', payload: { name: 'removeFromCart', trigger: 'event' } }],
          },
          {
            category: 'datasource',
            summary: 'Add REST datasource "cartApi"',
            actions: [{ type: 'addDatasource', payload: { blockName: ctx.selectedBlockName ?? 'main', name: 'cartApi', type: 'rest', url: '/api/cart', method: 'POST' } }],
          },
        ],
      }],
    }),
  },
]

// ─── MockChatService ───────────────────────────────────────────

export class MockChatService implements ChatService {
  private delayMs: number

  constructor(delayMs = 20) {
    this.delayMs = delayMs
  }

  async *sendMessage(
    messages: Array<{ role: string; content: string }>,
    context: ChatContext,
  ): AsyncGenerator<StreamChunk> {
    const content = messages[messages.length - 1]?.content ?? ''
    const phase = context.phase ?? 'idle'

    // Phase 4 IA-4 smoke-path — when the user asks "what page am I on?" or
    // "what shell" or "where am I" and we have a pageContextInstructions
    // block from the adapter, echo it back. This is mock-only and gives
    // Playwright a deterministic signal that auto-injection is wired.
    if (context.pageContextInstructions && /\b(where am i|what (?:page|shell|route))\b/i.test(content)) {
      const reply = `You're here:\n${context.pageContextInstructions}`
      for (const char of reply) {
        if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs))
        yield { type: 'text', content: char }
      }
      return
    }

    // Check composite patterns first (context-aware, higher priority)
    for (const { pattern, buildResponse } of compositePatterns) {
      if (pattern.test(content)) {
        const compositeResponse = buildResponse(context)
        // Stream text
        for (const char of compositeResponse.text) {
          if (this.delayMs > 0) {
            await new Promise((r) => setTimeout(r, this.delayMs))
          }
          yield { type: 'text', content: char }
        }
        yield { type: 'composite', actions: compositeResponse.composite }
        return
      }
    }

    // Pipeline mode: check if we're mid-pipeline or triggering one
    let intent: IntentResponse

    if (phase !== 'idle' && phase !== 'complete') {
      // We're in an active pipeline phase
      if (phase === 'gathering') {
        // User is providing details → confirm narrative
        intent = getConfirmNarrativeResponse(content, context)
      } else {
        // Auto-advance through remaining phases
        intent = getPipelineResponse(phase, content, context)
      }
    } else {
      // Check simple intents first (higher priority for specific commands)
      const simpleIntent = detectIntent(content)
      if (simpleIntent !== defaultResponse) {
        // Matched a known simple intent
        intent = simpleIntent
      } else if (isPipelineTrigger(content)) {
        // No simple intent match → try pipeline trigger
        intent = getPipelineResponse('idle', content, context)
      } else {
        // Fall back to default
        intent = simpleIntent
      }
    }

    // Stream text character by character
    for (const char of intent.text) {
      if (this.delayMs > 0) {
        await new Promise((r) => setTimeout(r, this.delayMs))
      }
      yield { type: 'text', content: char }
    }

    // Yield actions if present
    if (intent.actions) {
      yield { type: 'actions', actions: intent.actions }
    }

    // Yield mode if present
    if (intent.targetMode) {
      yield { type: 'mode', mode: intent.targetMode }
    }

    // Yield scroll target if present
    if (intent.scrollTarget) {
      yield { type: 'scroll', target: intent.scrollTarget }
    }
  }
}
